import React, { useState } from "react";
import { GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { UploadButton } from "@presentation/components/helpers/UploadButton";
import { RemoteFolderExplorerGrid } from "@presentation/components/helpers/RemoteFolderExplorerGrid";
import { ExplorerItem, buildRowKey, normalizePathValue } from "@presentation/components/helpers/useRemoteFolderExplorer";
import { useSettingsExplorerSlice } from "@presentation/stores/SoftwareSettingsExplorerStore";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { DeleteSoftwareSettingsService, ListSoftwareSettingsService } from "@application/useCases";
import { useFastaFilesLocationName } from "@presentation/hooks/useStorageLocations";
import { buildRootBreadcrumbLabel } from "@presentation/stores/FileExplorerStore";
import { PathTypeSchema, SettingsTypeEnumSchema, SoftwareSettingsMetadata } from "@domain/schemas/BI";
import { UploadSettingsDialog } from "@presentation/components/UploadSettingsDialog";
import type { UploadDestination } from "@presentation/components/uploadDestination/UploadDestinationPickerDialog";
import { normalizeFolderKey } from "@presentation/components/helpers/useRemoteFolderExplorer";

const getVersionMajor = (version?: string | null) => {
  const normalized = (version ?? "").trim().replace(/^(?:SN|SM)/i, "");
  const match = normalized.match(/^v?(\d+)\.\d+\.\d+(?:[.-].*)?$/i);
  return match ? match[1] : null;
};

interface SoftwareSettingsExplorerGridProps {
  settingsType: SettingsTypeEnumSchema;
  selection: "single" | "multi";
  softwareScoped: boolean;
  label: string;
  /**
  Called with the full resolved metadata (not just path/size) so the caller can seed `useJobSetupStore`'s typed setter.
  */
  onSelectionChange: (selected: SoftwareSettingsMetadata[]) => void;
}

/**
 * SoftwareSettings facet of the shared `RemoteFolderExplorerGrid` core. Unlike Raw/Fasta,
 * `SoftwareSettingsMetadata` carries no size, so the size column is hidden, and selection needs the FULL
 * metadata object (software/version, for the compatibility badge) rather than just a path - the generic
 * hook only tracks `{path, sizeInBytes, sourcePath}`, so the full objects are cached locally by `rowKey`
 * as they're listed, and `onSelectionChange`'s bare paths are resolved back against that cache.
 */
export const SoftwareSettingsExplorerGrid: React.FC<SoftwareSettingsExplorerGridProps> = ({ settingsType, selection, softwareScoped, label, onSelectionChange }) => {
  const listSoftwareSettingsService = useInjection(ListSoftwareSettingsService);
  const deleteSoftwareSettingsService = useInjection(DeleteSoftwareSettingsService);
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const software = useJobSetupStore((state) => state.CreateJobState.software);
  const softwareVersion = useJobSetupStore((state) => state.CreateJobState.softwareVersion);
  const store = useSettingsExplorerSlice(settingsType);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const metadataByRowKeyRef = React.useRef<Map<string, SoftwareSettingsMetadata>>(new Map());

  // The store seeds the root crumb from /config.json so something is shown immediately; the backend is the
  // source of truth (Azure container / AWS folder), so the label is corrected as soon as it answers.
  const fastaFilesLocationName = useFastaFilesLocationName();
  const { breadcrumbItems, setBreadcrumbItems } = store;
  const currentBreadcrumb = breadcrumbItems.at(-1);
  const initialDestination = React.useMemo<Partial<UploadDestination>>(() => {
    const sourcePath = currentBreadcrumb?.sourcePath;
    const currentPath = normalizeFolderKey(currentBreadcrumb?.id ?? "").replace(/\/+$/, "");
    const rootPath = normalizeFolderKey(sourcePath ?? "").replace(/\/+$/, "");
    const rootPrefix = rootPath ? `${rootPath}/` : "";
    let folder = currentPath;
    if (currentPath === rootPath) {
      folder = "";
    } else if (rootPrefix && currentPath.startsWith(rootPrefix)) {
      folder = currentPath.slice(rootPrefix.length);
    }

    return {
      rootPath: sourcePath ?? undefined,
      folder,
    };
  }, [currentBreadcrumb]);
  React.useEffect(() => {
    const rootLabel = buildRootBreadcrumbLabel(fastaFilesLocationName);
    const rootItem = breadcrumbItems[0];
    if (!rootItem || rootItem.id !== "home" || rootItem.text === rootLabel) {
      return;
    }

    setBreadcrumbItems(breadcrumbItems.map((item, index) => (index === 0 ? { ...item, text: rootLabel } : item)));
  }, [fastaFilesLocationName, breadcrumbItems, setBreadcrumbItems]);

  const getCompatibility = React.useCallback(
    (item: SoftwareSettingsMetadata) => {
      if (softwareScoped && software && item.software && item.software !== software) {
        return { compatible: false, reason: `Uploaded for ${item.software}, selected ${software}` };
      }
      const selectedMajor = getVersionMajor(softwareVersion);
      const detectedMajor = item.detectedMajorVersion === null || item.detectedMajorVersion === undefined ? null : String(item.detectedMajorVersion);
      if (!selectedMajor || !detectedMajor) return { compatible: true, reason: "" };
      const isCompatible = detectedMajor === selectedMajor;
      return {
        compatible: isCompatible,
        reason: isCompatible ? `Compatible with ${software ?? "Spectronaut"} ${selectedMajor}` : `Detected ${software ?? "Spectronaut"} ${detectedMajor}, selected ${selectedMajor}`,
      };
    },
    [software, softwareScoped, softwareVersion],
  );

  return (
    <RemoteFolderExplorerGrid<SoftwareSettingsMetadata>
      activeProjectId={activeProjectId}
      pathType={PathTypeSchema.SoftwareSettings}
      deleteFile={(target) => deleteSoftwareSettingsService.executeAsync(target.projectId, target.fileName, settingsType, target.folder || undefined, target.sourcePath)}
      selectionMode={selection}
      regexFilterStorageKey="settingsRegex"
      fileNameColumnTitle={label}
      showSizeColumn={false}
      store={store}
      fetchList={(folder, sourcePath) => listSoftwareSettingsService.getSettingsListAsync(activeProjectId ?? "", settingsType, softwareScoped ? software : undefined, folder, sourcePath)}
      toExplorerItem={(item) => {
        const location = normalizePathValue(item.location);
        const name = String(item.name ?? "");
        const isFolder = name.trim().length === 0;
        const id = isFolder ? location : [location, name].filter(Boolean).join("/");
        metadataByRowKeyRef.current.set(buildRowKey(id, item.sourcePath), item);
        return { location: item.location, fileName: item.name, sizeInBytes: 0, sourcePath: item.sourcePath };
      }}
      onSelectionChange={(selected) => {
        const resolved = selected
          .map((file) => metadataByRowKeyRef.current.get(buildRowKey(file.path, file.sourcePath ?? null)))
          .filter((metadata): metadata is SoftwareSettingsMetadata => !!metadata);
        onSelectionChange(resolved);
      }}
      toolbarActions={() => <UploadButton onClick={() => setShowUploadDialog(true)} title={`Upload ${label}`} />}
      dialog={(api) =>
        showUploadDialog && (
          <UploadSettingsDialog
            settingsType={settingsType}
            initialDestination={initialDestination}
            onCancel={() => setShowUploadDialog(false)}
            onUploadSuccess={() => {
              setShowUploadDialog(false);
              void api.refresh();
            }}
          />
        )
      }
    >
      <GridColumn
        field="compatibility"
        title="Compatibility"
        width="220px"
        sortable={false}
        filterable={false}
        cell={(properties: GridCustomCellProps) => {
          const dataItem = properties.dataItem as ExplorerItem;
          if (dataItem.isFolder) return <td></td>;
          const metadata = metadataByRowKeyRef.current.get(dataItem.rowKey);
          if (!metadata) return <td></td>;
          const { compatible, reason } = getCompatibility(metadata);
          if (!reason) return <td></td>;
          return (
            <td>
              <span style={{ color: compatible ? "#166534" : "#b91c1c", fontSize: "0.85em" }}>{reason}</span>
            </td>
          );
        }}
      />
    </RemoteFolderExplorerGrid>
  );
};

export default SoftwareSettingsExplorerGrid;
